import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { AppContext } from '../../../../providers/AppProvider';
import { FindContentShareType } from '../PostCard/PostCard.view';
import { CustomParagraph } from '../../../../models/slate-editor';
import { deserializeText } from '../../../PostEditor/TextEditor/helpers/serializer';
import {
  MediaTypePostEditorState,
  PostEditorContextEnum,
  PostEditorLink,
  PostEditorState
} from '../../../../models/post-editor';
import {
  LinkDestination,
  LinkGoogleCallToAction,
  MediaOrigin,
  MediaProportions,
  MediaType,
  ThirdPartyPost,
  ThirdPartyPostAttachmentType,
  ThirdPartyPostMediaType
} from '../../../../models/graphql';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { useMediaUpload } from '../../../../hooks/useMediaUpload';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import updateImmutable from 'immutability-helper';
import { initialParagraph, InitialPostEditorState } from '../../../PostEditor/states/initial';
import { usePostLinkSettings } from '../../../../hooks/usePostLinkSettings';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { makeFinalLink } from '../../../PostEditor/states/reducers/link.reducer';
import { BaseLinkSettings } from '../../../PostEditor/TextEditor/helpers/onChange';
import { MediaUploadStatus } from '../../../../constants/media';
import { FindContentContext } from '../FindContentProvider';
import { getPostSourceFrom3rdPartySource, setPostSource } from '../../../PostEditor/helpers/setPostSource';
import { getUploadIdFromFileOrUrl } from '../../../../helpers/media';
import { BeaconContext } from '../../../../providers/BeaconProvider';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type FindContentShareButtonViewProps = {
  post: ThirdPartyPost;
  isLinkPresent: boolean;
};

const createInitialPostEditor = (
  text: CustomParagraph[],
  link: PostEditorLink | null,
  urls: string[]
): PostEditorState => {
  const transformedMedia: MediaTypePostEditorState[] = urls
    ? urls.map(url => {
        return {
          __typename: 'Photo',
          loading: true,
          status: MediaUploadStatus.PENDING,
          gif: false,
          length: null,
          url: '',
          id: getUploadIdFromFileOrUrl(url),
          thumbnailUrl: '',
          origin: MediaOrigin.WEB,
          type: MediaType.PHOTO,
          height: 0,
          width: 0,
          archived: false,
          proportions: MediaProportions.SQUARE,
          transcoded: false,
          externalUpload: false
        };
      })
    : [];
  return updateImmutable(InitialPostEditorState(), {
    postGroups: {
      0: {
        groupPostAttributes: {
          content: {
            texts: {
              $set: [
                {
                  id: 1,
                  text
                }
              ]
            },
            link: {
              $set: link
            },
            media: {
              $set: transformedMedia
            }
          }
        }
      }
    }
  });
};

export const FindContentShareButtonView = (props: FindContentShareButtonViewProps): JSX.Element => {
  const { post, isLinkPresent } = props;
  const { queueUploads } = useMediaUpload();
  const { beacon } = useContext(BeaconContext);
  const hasMixedMedia =
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.VIDEO) &&
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.PHOTO);
  const { isTemporallySuspended } = useSubscriptionStatus();
  const { setPostEditorContext, selectedWorkspaceId, setSuspendedModalOpen } = useContext(AppContext);
  const { platform, platformType } = useContext(FindContentContext);

  const { loading, error, ...restLinkSettings } = usePostLinkSettings();
  useEffect(() => {
    if (error) {
      triggerErrorToast('Error getting link settings')(error);
    }
  }, [error]);

  const processPostEditorContext = useCallback(
    (type: FindContentShareType) => {
      if (!selectedWorkspaceId || loading || error) return;
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      const { defaultBitly, utmTracking, campaignTracking, shouldRemoveUtm } = restLinkSettings;
      const linkSettings: BaseLinkSettings = {
        destination: LinkDestination.INITIAL,
        useBitly: !!defaultBitly,
        googleCallToAction: LinkGoogleCallToAction.NONE,
        utmTracking: utmTracking,
        addUtm: campaignTracking,
        removeUtm: shouldRemoveUtm,
        removePreview: false,
        linkMetadata: undefined
      };
      const link: PostEditorLink | null =
        post.linkUrl && type !== FindContentShareType.IMAGE
          ? {
              ...linkSettings,
              initialUrl: post.linkUrl,
              url: makeFinalLink({ ...linkSettings, initialUrl: post.linkUrl })
            }
          : null;

      const text: CustomParagraph[] =
        type === FindContentShareType.ALL ? deserializeText(post.text, [], link) : [initialParagraph()];

      const urls: string[] = (() => {
        if (type !== FindContentShareType.LINK) {
          const videoMedia = post.media.find(m => m.type === ThirdPartyPostMediaType.VIDEO);

          if (videoMedia && videoMedia.thumbnailUrl && !hasMixedMedia) {
            return [videoMedia.thumbnailUrl];
          }

          const photoMediaUrls = post.media
            .filter(m => m.type === ThirdPartyPostMediaType.PHOTO)
            .map(media => String(media.url));

          if (photoMediaUrls.length > 0) {
            if (videoMedia && videoMedia.thumbnailUrl && hasMixedMedia) {
              return [videoMedia.thumbnailUrl, ...photoMediaUrls];
            } else {
              return photoMediaUrls;
            }
          }
        }

        return [];
      })();

      const postSource = !!platform && getPostSourceFrom3rdPartySource(platform, platformType, false);
      if (!postSource) return;
      const stateWithLinks = createInitialPostEditor(text, link, urls);
      const initialState = setPostSource(stateWithLinks, postSource);
      setPostEditorContext({
        type: PostEditorContextEnum.new,
        initialState,
        ...(urls.length
          ? {
              payload: {
                type: 'mediaUploadExternalCallback',
                payload: {
                  context: 'url',
                  urls,
                  selectedWorkspaceId,
                  queueUploads,
                  customizeIndex: undefined,
                  postGroupIndex: 0,
                  beacon
                }
              }
            }
          : {})
      });
    },
    [
      hasMixedMedia,
      post,
      queueUploads,
      selectedWorkspaceId,
      setPostEditorContext,
      loading,
      error,
      restLinkSettings,
      platformType,
      platform,
      beacon,
      isTemporallySuspended,
      setSuspendedModalOpen
    ]
  );

  const listItems = useMemo(() => {
    const items: ContainerListItem[] = [];
    // If we have photos attach, just make sure we have the media for it
    // If we have no photos, but media (assumed video), then we make sure we have the thumbnailUrl
    const showShareImage =
      (post.media.length > 0 && post.attachmentTypes.includes(ThirdPartyPostAttachmentType.PHOTO)) ||
      (post.media.length > 0 &&
        !post.attachmentTypes.includes(ThirdPartyPostAttachmentType.PHOTO) &&
        post.media.some(m => m.thumbnailUrl));
    const showShareFullPost = post.text.length > 0 || (showShareImage && isLinkPresent);

    if (showShareFullPost) {
      items.push({
        label: 'Share Full Post',
        action: () => processPostEditorContext(FindContentShareType.ALL)
      });
    }

    if (showShareImage) {
      items.push({
        label: 'Share Image',
        action: () => processPostEditorContext(FindContentShareType.IMAGE)
      });
    }

    if (isLinkPresent) {
      items.push({
        label: 'Share Link',
        action: () => processPostEditorContext(FindContentShareType.LINK)
      });
    }

    return items;
  }, [isLinkPresent, post, processPostEditorContext]);

  return (
    <ButtonMenu
      elementType={'button'}
      isSquareButton={false}
      side={'left'}
      size={'medium'}
      variant={'hollow'}
      expanded={true}
      listItems={listItems}
      label={'Share'}
    />
  );
};
